/**
 * RDV en ligne (#429) — fixed V1 constants.
 *
 * Horizon and rounding are hardcoded in V1 (FR6/FR7); making them configurable
 * is post-V1. See `docs/spec/rdv-en-ligne/adr-moteur-et-contrats.md`.
 */
// Search horizon for proposal generation, in days (FR6 — fixed in V1). Also
// bounds the submission window: `requestedStartAt` must land in
// [now, now + RDV_HORIZON_DAYS] wall-clock Paris (#623 F8) — anything outside
// is data pollution (a stale slot, or a bot picking year 2099).
export const RDV_HORIZON_DAYS = 90;
// Anti-pollution cap on the number of `pending` `RdvRequest` a company can
// accumulate (#623 F5). A real pro's inbox never carries 50 open demandes; this
// is a hard ceiling on the abuse noise a single company can receive, NOT a
// business rule the pro is expected to hit. The 51st public submission returns
// 429 with a French message. The COUNT+INSERT is not atomic and that is fine —
// a few units of drift under contention is anti-abuse, not accounting.
export const MAX_PENDING_RDV_REQUESTS_PER_COMPANY = 50;
// Slot start rounding step, in minutes (FR7 — fixed in V1).
export const RDV_ROUNDING_MINUTES = 15;
/**
 * Tolérance d'arrondi de palier (#870, arbitrage Hugo/Julien du 29/07/2026,
 * clôture #712) : jusqu'à cette marge, un trajet qui dépasse légèrement un
 * palier de `RDV_ROUNDING_MINUTES` peut quand même utiliser le palier
 * inférieur — le risque théorique d'arriver en retard de quelques minutes
 * vaut mieux qu'un trou d'attente de 15 min complet. Mécanisme (tolérance sur
 * l'INSTANT D'ARRIVÉE, jamais sur la durée brute, des deux côtés d'un trou
 * collé D20/D23) dans `engine.ts` ; doctrine complète dans
 * `docs/context/rdv-en-ligne.md` (§ Doctrines de fond).
 */
export const RDV_ROUNDING_TOLERANCE_MINUTES = 3;
// The only travel-time values a pro can pick, in minutes (Q1 — time only, no distance).
export const MAX_TRAVEL_TIME_MINUTES = [15, 30, 45, 60];
// Default travel-time value pre-selected when the pro has never saved (in minutes).
export const RDV_DEFAULT_MAX_TRAVEL_TIME_MINUTES = 30;
// Default distance guard-rail, in kilometers (#506). Unlike horizon/rounding this
// is configurable per company — the default is what a fresh company starts with
// and what the UI pre-fills. The travel-time provider (Epic 2.5b) reads the stored
// value (falling back to this default when null), and enforces it as a bird-flight
// pre-filter; the pure engine never sees a distance.
export const RDV_DEFAULT_MAX_TRAVEL_DISTANCE_KM = 150;
// Maximum number of proposals returned to the client (FR18 — top-5 brut).
export const MAX_PROPOSALS = 5;
export const RDV_PROPOSAL_LIMIT = 10;
// #734 — Matrix scope for a public slot search. One search performs at most
// ONE travel-time matrix request; its scope is the top candidates by
// bird-flight proxy (`orderCandidatesByProxy`), bounded BOTH by candidate
// count and by the unique-point count the matrix may carry (the routing
// layer caps a matrix side at 50 — `MATRIX_MAX_SIDE`; ORS bills by cell,
// documented ~3500 pairs for driving-car, and 50×50 = 2500 stays under).
// When either bound truncates the candidate list, a zero-survivor walk is
// answered 503 (inconclusive), NEVER `out_of_zone` — the unmeasured
// candidates might have survived (#734 acceptance criteria).
export const RDV_MATRIX_TOP_CANDIDATES = 40;
export const RDV_MATRIX_MAX_POINTS = 50;
// Detour equality band, in minutes (D12): two candidates whose marginal detour
// differs by at most this are treated as tied → the earliest date wins.
export const RDV_DETOUR_TIE_BAND_MINUTES = 2;
/**
 * Outlier-detour cap ratio (#713): during the planAppointments walk, a
 * candidate whose marginal detour exceeds this fraction of the MEAN of the
 * two travel limits applicable to ITS edges (toFirst/between/toReturn — the
 * same per-edge mapping constrainCandidate enforces; harden H1) is skipped —
 * the walk keeps searching for up to MAX_PROPOSALS under-cap survivors
 * instead of surfacing an aberrant slot (e.g. a 45-km outlier among ≤20-km
 * proposals, #478 recette 16/07). Indexed on the pro's own travel setting so
 * it self-calibrates urban/rural (cap 60 min → cut at 30 min of detour) —
 * see the arbitration on #713 for the rejected alternatives (gap-vs-best,
 * %-of-best, fixed global cap, median).
 * Guard-rail: the cap never empties the result. Two distinct branches
 * (#888, generalized from the single-best repêchage of #713/#879), both
 * conditioned on no candidate having genuinely passed the cap on its own
 * edges (a genuine survivor blocks ALL over-cap promotion outright — see
 * `hasGenuineUnderCap` in `walkAndSelect`): with NO survivor admitted at
 * all (agenda over-cap for its whole duration), only the single cheapest
 * over-cap candidate is promoted back in — unchanged since before #879.
 * With at least one EXEMPTED survivor already admitted, every over-cap
 * candidate that beats its detour is promoted — not just the cheapest.
 * `selectProposals` still trims to MAX_PROPOSALS downstream either way.
 * Empty-day exemption (#879): a `departure → return` candidate with no
 * out-of-window neighbor is NOT judged against this cap — see the invariant
 * comment in `walkAndSelect` (engine.ts) for the full rationale. Do not
 * "fix" the exemption from here by tightening this ratio.
 */
export const RDV_OUTLIER_DETOUR_RATIO = 0.5;
// Same-place snap threshold, in km (#478 defect 3 — recette canary 16/07/2026,
// "La Cribotière"/"Le Moulin Cassé"/"La Guérinais"). A single real lieu-dit
// geocodes to slightly different coordinates depending on the source (an
// agenda event's stored coords vs the client's tunnel-entered address) —
// dispersion of a few tens/hundreds of meters is normal geocoding noise, not
// a real trip. Below this threshold the engine treats the two points as the
// SAME place and charges zero travel, without querying the routing provider:
// no real car trip needs provisioning under ~300m, and road-routing
// granularity does not meaningfully resolve a leg that short anyway.
export const SAME_PLACE_EPSILON_KM = 0.3;
// NOTE (#734): `MAX_ROUTING_CALLS_PER_COMPUTATION` (the #621 per-computation
// routing-call backstop) was removed with the per-leg `RdvTravelTimeProvider` —
// a planning run performs at most ONE matrix request by construction, so there
// are no per-leg calls left to count.
// Conservative bird-flight → road-time lower bound, in minutes per kilometer.
// Reads as: at the max plausible door-to-door average speed
// (`MAX_PLAUSIBLE_DOOR_TO_DOOR_KMH`), a straight-line distance of X km takes AT
// LEAST X × `MIN_MINUTES_PER_KM` minutes on the actual road (road distance ≥
// bird distance, road speed ≤ this max).
//
// #621 introduced a provider-level `birdFlightKm × MIN_MINUTES_PER_KM >
// maxTravelTimeMinutes` short-circuit built on this bound; #816 REMOVED it
// from `PreloadedTravelTimeProvider` (the matrix is already fetched in full
// per search, so short-circuiting a cell saved no network call — its only
// effect was to make a #787-waived same-place leg unmeasurable purely from a
// bird-flight guess). The formula's proof of neutrality for every OTHER leg is
// exactly what justified dropping it: `bird × MIN_MINUTES_PER_KM > cap` already
// implies `realTime > cap`, so removing the guess changes nothing for a
// non-waived leg — the engine's own cap check still rejects it, just measured
// rather than guessed. The distance-only pre-filter (#506) is unaffected and
// still lives in the provider.
//
// **Direction of safety** (still the reasoning this constant must satisfy,
// even test-only) — sub-estimating the max speed (higher `MIN_MINUTES_PER_KM`)
// would create FALSE REJECTS (an edge routinely feasible under `maxTravelTime`
// short-circuited to `Infinity`), which is FORBIDDEN by the ADR
// (`docs/spec/rdv-en-ligne/adr-moteur-et-contrats.md`, "proxy = order only,
// never rejection"). Over-estimating the max speed (lower `MIN_MINUTES_PER_KM`)
// only makes the filter more permissive: fewer edges short-circuited, more
// routing calls, but zero false rejects — inoffensive. When in doubt, keep the
// speed generous.
//
// 130 km/h is a comfortably high ceiling: French highway speed limits cap at
// 130, and the bird-flight distance is by construction ≤ the road distance, so
// a door-to-door speed sustainably above 130 km/h against a bird-flight
// yardstick is not physically realistic on a road network. File-local: the
// derived `MIN_MINUTES_PER_KM` is the only value consumed elsewhere (test
// assertions of the neutrality proof above, since #816); leaking the reasoning
// constant would widen the shared surface without a caller.
const MAX_PLAUSIBLE_DOOR_TO_DOOR_KMH = 130;
export const MIN_MINUTES_PER_KM = 60 / MAX_PLAUSIBLE_DOOR_TO_DOOR_KMH;
//# sourceMappingURL=constants.js.map